/**
 * Projéteis reciclados (pool) — flechas/bolts. Viajam de verdade, colidem, causam dano
 * e voltam ao pool. Homing leve opcional (assistência mobile). Geometria/material
 * compartilhados (nada de alocar por disparo). Preparado para arrows/magic bolts.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { Pool } from "../utils/Pool";
import { dealDamage } from "./DamageSystem";
import type { WeaponType } from "../items/EquipmentTypes";
import { events } from "../core/Events";
import type { CombatStats } from "./CombatStats";
import type { Damageable } from "./Damageable";
import type { DamageType } from "../items/EquipmentTypes";
import type { Sanctuary } from "../world/Sanctuary";
import { wallBetween } from "../world/TerritoryGates";

interface Shot {
  mesh: THREE.Object3D;
  /** 10.9CB — o tiro tem DUAS silhuetas: flecha (arco) e orbe arcano (cajado). Só uma fica visível. */
  arrow: THREE.Object3D;
  orb: THREE.Object3D;
  orbMats: THREE.MeshBasicMaterial[];
  spin: number;
  dir: THREE.Vector3;
  speed: number;
  life: number;
  radius: number;
  knockback: number;
  damageType: DamageType;
  weaponType?: WeaponType;
  stats: CombatStats;
  target: Damageable | null;
  alive: boolean;
  pierce: boolean;
  hits: Set<Damageable>;
}

const _tmp = new THREE.Vector3();
const _prev = new THREE.Vector3();
const _impact = new THREE.Vector3();
const _white = new THREE.Color(0xffffff);
const MAX_PIERCE = 4;

export interface FireOptions {
  target?: Damageable | null;
  radiusScale?: number; // voidShot = maior
  speedScale?: number;
  pierce?: boolean; // atravessa vários alvos (Void Shot)
  /** 10.6G — arma que disparou (arco/lança de arremesso): leva a fraqueza junto. */
  weaponType?: WeaponType;
  /** 10.9CB — cor de energia da arma (tinge o orbe do cajado). */
  color?: number;
}

export class ProjectileManager {
  private readonly active: Shot[] = [];
  private readonly pool: Pool<Shot>;
  // As peças são compartilhadas entre o pool: uma flecha continua barata, mas tem
  // haste, ponta e penas reconhecíveis em vez de um único cone luminoso.
  private readonly shaftGeo = new THREE.CylinderGeometry(0.026, 0.032, 0.72, 6);
  private readonly tipGeo = new THREE.ConeGeometry(0.095, 0.27, 5);
  private readonly fletchingGeo = new THREE.BoxGeometry(0.15, 0.018, 0.2);
  private readonly shaftMat = new THREE.MeshStandardMaterial({ color: 0x50372a, roughness: 0.72, metalness: 0.05, flatShading: true });
  private readonly tipMat = new THREE.MeshStandardMaterial({ color: 0xd9e4fb, emissive: 0x567cff, emissiveIntensity: 0.55, roughness: 0.24, metalness: 0.9, flatShading: true });
  private readonly fletchingMat = new THREE.MeshBasicMaterial({ color: 0x9d7dff, transparent: true, opacity: 0.92 });
  // 10.9CB — peças do orbe arcano (cajado), compartilhadas entre os tiros
  private readonly orbGeo = new THREE.IcosahedronGeometry(0.17, 0);
  private readonly haloGeo = new THREE.RingGeometry(0.22, 0.34, 16);
  private readonly shardGeo = new THREE.OctahedronGeometry(0.07, 0);
  private sanctuary: Sanctuary | null = null;

  /** Injeta a Sanctuary: projéteis ofensivos são destruídos na barreira da safe zone. */
  setSanctuary(sanctuary: Sanctuary): void {
    this.sanctuary = sanctuary;
  }

  constructor(private readonly scene: THREE.Scene) {
    this.pool = new Pool<Shot>(
      () => this.make(),
      (s) => {
        s.mesh.visible = false;
        s.alive = false;
      },
      8,
    );
  }

  fire(
    origin: THREE.Vector3,
    dirX: number,
    dirZ: number,
    speed: number,
    stats: CombatStats,
    damageType: DamageType,
    knockback: number,
    opts?: FireOptions,
  ): void {
    const s = this.pool.acquire();
    // Sai da mão/arco, e não do centro dos pés do Hunter.
    s.mesh.position.set(origin.x, origin.y + 0.05, origin.z);
    s.dir.set(dirX, 0, dirZ);
    if (s.dir.lengthSq() < 1e-6) s.dir.set(0, 0, 1);
    s.dir.normalize();
    s.speed = speed * (opts?.speedScale ?? 1);
    s.life = GameConfig.projectile.lifetime;
    s.radius = GameConfig.projectile.radius * 3 * (opts?.radiusScale ?? 1);
    s.knockback = knockback;
    s.damageType = damageType;
    s.weaponType = opts?.weaponType;
    s.stats = stats;
    s.target = opts?.target ?? null;
    s.pierce = opts?.pierce ?? false;
    s.hits.clear();
    s.alive = true;
    s.mesh.rotation.y = Math.atan2(s.dir.x, s.dir.z);
    // 10.9CB — CAJADO atira MAGIA, não flecha (o mago e o necromante usavam a mesma flecha do arco).
    const magic = opts?.weaponType === "staff";
    s.arrow.visible = !magic;
    s.orb.visible = magic;
    s.spin = 0;
    if (magic) {
      const c = opts?.color ?? 0x9d7bff;
      for (const m of s.orbMats) m.color.setHex(c);
      s.orbMats[0].color.lerp(_white, 0.55); // núcleo mais claro que o halo
    }
    const sc = opts?.radiusScale ?? 1;
    s.mesh.scale.setScalar(sc);
    s.mesh.visible = true;
    this.active.push(s);
  }

  update(dt: number, targets: readonly Damageable[]): void {
    const assist = GameConfig.projectile.aimAssist;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const s = this.active[i];
      s.life -= dt;
      if (s.orb.visible) { // 10.9CB — o orbe gira e pulsa enquanto voa
        s.spin += dt;
        s.orb.rotation.y = s.spin * 6;
        s.orb.rotation.z = s.spin * 3;
        s.orb.scale.setScalar(1 + Math.sin(s.spin * 14) * 0.12);
      }

      // homing leve em direção ao alvo (se vivo)
      if (s.target && !s.target.isDead && assist > 0) {
        _tmp.subVectors(s.target.position, s.mesh.position);
        _tmp.y = 0;
        if (_tmp.lengthSq() > 1e-4) {
          _tmp.normalize();
          s.dir.lerp(_tmp, assist).normalize();
          s.mesh.rotation.y = Math.atan2(s.dir.x, s.dir.z);
        }
      }

      _prev.copy(s.mesh.position);
      s.mesh.position.addScaledVector(s.dir, s.speed * dt);

      // BARREIRA DA SANCTUARY: qualquer projétil que cruze para dentro da safe zone é
      // destruído na fronteira (sem dano). Protege o Player e futuros inimigos ranged.
      if (this.sanctuary && this.sanctuary.projectileCrossing(_prev, s.mesh.position, _impact)) {
        events.emit("sanctuary:projectileBlocked", { position: _impact.clone() });
        this.pool.release(s);
        this.active.splice(i, 1);
        continue;
      }

      // 10.6O: projétil que cruzou uma parede é destruído
      if (wallBetween(_prev.x, _prev.z, s.mesh.position.x, s.mesh.position.z)) {
        this.pool.release(s);
        this.active.splice(i, 1);
        continue;
      }

      // colisão com alvo(s) vivo(s)
      let consumed = false;
      for (const t of targets) {
        if (t.isDead || s.hits.has(t)) continue;
        const tr = (t as { radius?: number }).radius ?? 0.5;
        const dx = t.position.x - s.mesh.position.x;
        const dz = t.position.z - s.mesh.position.z;
        const reach = s.radius + tr;
        if (dx * dx + dz * dz <= reach * reach) {
          dealDamage(t, s.stats, {
            targetKind: "enemy", byPlayer: true,
            damageType: s.damageType,
            weaponType: s.weaponType, // 10.6G
            knockbackFrom: s.mesh.position,
            knockbackForce: s.knockback,
          });
          s.hits.add(t);
          // sem pierce: consome no 1º acerto; com pierce: segue até MAX_PIERCE alvos
          if (!s.pierce || s.hits.size >= MAX_PIERCE) {
            consumed = true;
            break;
          }
        }
      }

      if (consumed || s.life <= 0) {
        this.pool.release(s);
        this.active.splice(i, 1);
      }
    }
  }

  clear(): void {
    for (const s of this.active) this.pool.release(s);
    this.active.length = 0;
  }

  private make(): Shot {
    const mesh = new THREE.Group();
    mesh.name = "void_arrow";
    const shaft = new THREE.Mesh(this.shaftGeo, this.shaftMat);
    shaft.rotation.x = Math.PI / 2;
    shaft.position.z = -0.04;
    const tip = new THREE.Mesh(this.tipGeo, this.tipMat);
    tip.rotation.x = Math.PI / 2;
    tip.position.z = 0.49;
    const featherA = new THREE.Mesh(this.fletchingGeo, this.fletchingMat);
    featherA.position.set(0, 0.055, -0.32);
    featherA.rotation.y = 0.18;
    const featherB = new THREE.Mesh(this.fletchingGeo, this.fletchingMat);
    featherB.position.set(0.055, 0, -0.32);
    featherB.rotation.z = Math.PI / 2;
    const arrow = new THREE.Group();
    arrow.add(shaft, tip, featherA, featherB);
    // 10.9CB — ORBE ARCANO (cajado): núcleo + halo + 2 lascas orbitando. Materiais PRÓPRIOS de cada
    // tiro (o pool é pequeno) pra poder tingir na cor da arma sem afetar os outros.
    const orb = new THREE.Group();
    const coreMat = new THREE.MeshBasicMaterial({ color: 0xd8c8ff, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false });
    const haloMat = new THREE.MeshBasicMaterial({ color: 0x9d7bff, transparent: true, opacity: 0.45, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false });
    const shardMat = new THREE.MeshBasicMaterial({ color: 0x9d7bff, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false });
    const core = new THREE.Mesh(this.orbGeo, coreMat);
    const halo = new THREE.Mesh(this.haloGeo, haloMat);
    halo.rotation.x = Math.PI / 2;
    const shardA = new THREE.Mesh(this.shardGeo, shardMat);
    shardA.position.set(0.22, 0, 0);
    const shardB = new THREE.Mesh(this.shardGeo, shardMat);
    shardB.position.set(-0.22, 0, 0);
    orb.add(core, halo, shardA, shardB);
    orb.visible = false;
    mesh.add(arrow, orb);
    mesh.traverse((o) => { o.frustumCulled = false; });
    mesh.visible = false;
    this.scene.add(mesh);
    return {
      mesh,
      arrow,
      orb,
      orbMats: [coreMat, haloMat, shardMat],
      spin: 0,
      dir: new THREE.Vector3(0, 0, 1),
      speed: 0,
      life: 0,
      radius: 0.5,
      knockback: 0,
      damageType: "piercing",
      stats: { baseDamage: 0, critChance: 0, critDamageMult: 1, armor: 0 },
      target: null,
      alive: false,
      pierce: false,
      hits: new Set<Damageable>(),
    };
  }
}
